<template>
  <div class="address-info">
    <div class="bg-white border border-border rounded-md lg:mb-5 mb-4">
      <div class="flex w-full border-b border-border !m-0 p-4 gap-[8px] items-center flex-wrap">
        <span class="number-badge"><span v-if="(state.opt_type != 'general' && state.opt_type != 'SEIU721')">1</span><span
            v-if="(state.opt_type == 'general' || state.opt_type == 'SEIU721')">2</span></span>
        <h2 class="card-title !m-0">
          Address Information
          <span v-if="isStudentFlow" class="student-address-help text-xs font-normal ml-1 normal-case">
            (Please use your student&apos;s address that they will be living at while at school)
          </span>
        </h2>
      </div>
      <div class="p-4">
        <div class="form-group mb-4">
          <label id="address-label" for="address" class="address-label font-normal text-mute text-sm mb-2 inline-block">
            {{ isStudentFlow ? 'Enter Address' : 'Your Address' }}<span v-if="!isStudentFlow" class="required-asterisk text-error">*</span>
          </label>
          <vue-google-autocomplete class="mt-1" id="address"
            classname="w-full text-black border border-blue-100 rounded py-2 px-4 mb-3 disabled:opacity-60" :placeholder="isStudentFlow ? 'Enter Address' : 'Enter Your Address'"
            ref="address" :disabled="disabled" country="us" :fields="googleAutocompleteFields" @blur="v$.form.address.$touch()" :types="['address']" v-model="form.address"
            v-on:placechanged="handlePlaceChange">
          </vue-google-autocomplete>
          <small v-if="v$.form.address.$error" class="text-error text-xs">
            Address is required.
          </small>
          <small class="mt-[8px] block text-xs font-normal" :class="isStudentFlow ? 'student-address-help' : 'text-mute'">
            <template v-if="isStudentFlow">
              As you type, addresses will appear. Select the correct address. Please use student&apos;s address that they will be living at while at school. If the address is unknown (example: your student has not been issued an address by the school), at a minimum enter the City and State below in the corresponding fields.
            </template>
            <template v-else>
              As you type, you will see a possible list of addresses. Select the correct address.
            </template>
          </small>
        </div>
        <div class="form-group mb-4">
          <label for="address1" class="address-label font-normal text-mute text-sm mb-2 inline-flex items-center gap-1">
            <span>Address Line 1<span v-if="!isStudentFlow" class="required-asterisk text-error">*</span></span>
            <span v-if="isStudentFlow" class="address-info-tooltip group">
              <button
                type="button"
                class="address-info-trigger"
                aria-describedby="address-line1-tooltip"
                aria-label="Address Line 1 help"
              >
                <span class="address-info-icon" aria-hidden="true">i</span>
              </button>
              <span id="address-line1-tooltip" role="tooltip" class="address-info-popover">
                <span class="address-info-popover__title">Address Unknown?</span>
                <span class="address-info-popover__body">If the address is unknown (example: student has not been issued an address by the school), please leave this blank.</span>
              </span>
            </span>
          </label>
          <input id="address1" type="text" :disabled="disabled" placeholder="Enter Address Line 1" class="form disabled:opacity-60" v-model="form.address1" @blur="v$.form.address1.$touch()" />
          <small v-if="v$.form.address1.$error" class="text-error text-xs">
            Address Line 1 is required.
          </small>
        </div>
        <div class="form-group mb-4">
          <label for="address2" class="address-label font-normal text-mute text-sm mb-2 inline-flex items-center gap-1">
            <span>Address Line 2</span>
            <span v-if="isStudentFlow" class="address-info-tooltip group">
              <button
                type="button"
                class="address-info-trigger"
                aria-describedby="address-line2-tooltip"
                aria-label="Address Line 2 help"
              >
                <span class="address-info-icon" aria-hidden="true">i</span>
              </button>
              <span id="address-line2-tooltip" role="tooltip" class="address-info-popover">
                <span class="address-info-popover__title">Address Unknown?</span>
                <span class="address-info-popover__body">If the address is unknown (example: student has not been issued an address by the school), please leave this blank.</span>
              </span>
            </span>
          </label>
          <input id="address2" type="text" :disabled="disabled" placeholder="Enter Address Line 2" class="form disabled:opacity-60" v-model="form.address2" />
        </div>
        <div class="grid grid-cols-1 xl:grid-cols-4 gap-4">
          <div>
            <label for="city" class="address-label font-normal text-mute text-sm mb-2 inline-block">
              City<span class="required-asterisk text-error">*</span>
            </label>
            <input id="city" type="text" :disabled="disabled" placeholder="Enter City" class="form disabled:opacity-60" v-model="form.city"
              @blur="v$.form.city.$touch()" />
            <small v-if="v$.form.city.$error" class="text-error text-xs">
              City is required.
            </small>
          </div>
          <div>
            <label for="state" class="address-label font-normal text-mute text-sm mb-2 inline-block">
              State<span class="required-asterisk text-error">*</span>
            </label>
            <div class="state-search-wrap">
              <input
                ref="stateInput"
                id="state"
                type="text"
                :disabled="disabled"
                placeholder="Enter State (e.g. FL)"
                class="form disabled:opacity-60"
                v-model="stateQuery"
                autocomplete="off"
                role="combobox"
                aria-autocomplete="list"
                :aria-expanded="showStateSuggestions ? 'true' : 'false'"
                :aria-activedescendant="stateActiveIndex >= 0 ? `state-option-${stateActiveIndex}` : undefined"
                aria-controls="state-suggestions"
                @input="onStateInput"
                @focus="onStateFocus"
                @blur="onStateBlur"
                @keydown="onStateKeydown"
              />
              <ul
                v-if="showStateSuggestions && filteredStateSuggestions.length"
                id="state-suggestions"
                class="state-suggestions"
                role="listbox"
              >
                <li
                  v-for="(st, index) in filteredStateSuggestions"
                  :id="`state-option-${index}`"
                  :key="st.abbreviation"
                  role="option"
                  :class="{ 'state-suggestions__option--active': index === stateActiveIndex }"
                  :aria-selected="index === stateActiveIndex ? 'true' : 'false'"
                  @mousedown.prevent="selectState(st)"
                  @mouseenter="stateActiveIndex = index"
                >
                  {{ st.abbreviation }}
                </li>
              </ul>
            </div>
            <small v-if="v$.form.state.$error" class="text-error text-xs">
              <template v-if="v$.form.state.required?.$invalid">State is required.</template>
              <template v-else>Please select a valid 2-letter state code.</template>
            </small>
          </div>
          <div>
            <label for="zip" class="address-label font-normal text-mute text-sm mb-2 inline-block">
              ZIP Code<span v-if="!isStudentFlow" class="required-asterisk text-error">*</span>
            </label>
            <input id="zip" type="text" :disabled="disabled" placeholder="Enter ZIP Code" class="form disabled:opacity-60" v-model="form.zip" @input="form.zip = form.zip.trim()" @blur="v$.form.zip.$touch()" />
            <small v-if="v$.form.zip.$error" class="text-error text-xs">
              ZIP Code is required.
            </small>
          </div>
          <div>
            <label for="county" class="address-label font-normal text-mute text-sm mb-2 inline-block">
              County
            </label>
            <input id="county" type="text" :disabled="disabled" placeholder="Enter County" class="form disabled:opacity-60" v-model="form.county" />
          </div>
        </div>
      </div>
    </div>
    <div v-if="state.enroll_msg" class="bg-red-500 border border-border rounded-md lg:mb-5 mb-4 hidden lg:block mt-5">
      <div class="p-4 flex items-start justify-start gap-4">
        <span
          class="w-[50px] h-[50px] bg-primaryLight border border-border flex rounded-md items-center justify-center shrink-0">
          <img src="@/assets/images/info.svg" alt="info">
        </span>
        <div>
          <p class="content !text-white">
            Thank you for your interest. At this time, U.S. Legal is not accepting enrollments in your state. If you
            have
            questions, please contact Customer Service at
           (800) 356-LAWS.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import { computed, onMounted, reactive, ref, watch } from "vue";
import Swal from "sweetalert2";
import VueGoogleAutocomplete from "vue-google-autocomplete"; // For address autocomplete from Google
import filter from "lodash/filter";
import { isStudentFlow as checkStudentFlow, effectiveOptType as getEffectiveOptType } from "@/utils/enrollmentFlow";

const GOOGLE_AUTOCOMPLETE_FIELDS = [
  "address_components",
  "formatted_address",
  "geometry",
  "name",
  "place_id",
  "type",
];

const getPostalCode = (rawPlace, fallbackText = "") => {
  for (const component of rawPlace?.address_components || []) {
    if (component.types?.includes("postal_code")) {
      return component.short_name;
    }
  }
  const match = String(
    rawPlace?.formatted_address || fallbackText || ""
  ).match(/\b(\d{5})(?:-\d{4})?\b/);
  return match?.[1] || "";
};

const geocodeZipFromLocation = (location) =>
  new Promise((resolve) => {
    if (!location || !window.google?.maps?.Geocoder) {
      resolve("");
      return;
    }
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location }, (results, status) => {
      if (status !== "OK" || !results?.length) {
        resolve("");
        return;
      }
      for (const result of results) {
        const zip = getPostalCode(result);
        if (zip) {
          resolve(zip);
          return;
        }
      }
      resolve("");
    });
  });

const EXCLUDED_ADDRESS_STATE_CODES = new Set([
  "AS",
  "FM",
  "GU",
  "MH",
  "MP",
  "PW",
  "PR",
  "VI",
]);

export default {
  name: "AddressInfoSection",
  props: {
    state: {
      type: Object,
    },
    disabled: {
      type: Boolean,
      default: false
    },
    searchObject: {
      type: Object,
    },
  },
  setup(props) {
    const isStudentFlow = computed(() => checkStudentFlow(props.state));

    // Reactive form data
    const form = reactive({
      address: "",
      address1: "",
      address2: "",
      city: "",
      state: "",
      zip: "",
      county: "",
    });

    const stateInput = ref(null);
    const stateQuery = ref("");
    const showStateSuggestions = ref(false);
    const stateActiveIndex = ref(-1);

    const addressStates = computed(() =>
      (props.state?.states || []).filter(
        (st) =>
          !EXCLUDED_ADDRESS_STATE_CODES.has(
            String(st?.abbreviation || "").toUpperCase()
          )
      )
    );
    const filteredStateSuggestions = computed(() => {
      const q = String(stateQuery.value || "").trim().toLowerCase();
      if (!q) return addressStates.value;
      return addressStates.value.filter((st) => {
        const name = String(st?.name || "").toLowerCase();
        const abbr = String(st?.abbreviation || "").toLowerCase();
        return name.includes(q) || abbr.includes(q);
      });
    });

    // Validation rules — address fields optional for student parameter; city/state still required
    const rules = computed(() => {
      const studentFlow = isStudentFlow.value;
      return {
        form: {
          address: studentFlow ? {} : { required },
          address1: studentFlow ? {} : { required },
          city: { required },
          state: {
            required,
            validState: (value) => {
              const v = String(value || "").trim().toUpperCase();
              if (!v) return true; // let `required` handle empty
              return addressStates.value.some(
                (st) => String(st?.abbreviation || "").toUpperCase() === v
              );
            },
          },
          zip: studentFlow ? {} : { required },
        },
      };
    });

    const v$ = useVuelidate(rules, { form });

    const onStateInput = () => {
      showStateSuggestions.value = true;
      stateActiveIndex.value = -1;
    };

    const onStateFocus = () => {
      showStateSuggestions.value = true;
    };

    const onStateBlur = () => {
      setTimeout(() => {
        showStateSuggestions.value = false;
        stateActiveIndex.value = -1;
        const q = String(stateQuery.value || "").trim().toUpperCase();
        const match = addressStates.value.find(
          (st) => String(st?.abbreviation || "").toUpperCase() === q
        );
        if (match) {
          form.state = match.abbreviation;
          stateQuery.value = match.abbreviation;
        } else {
          form.state = q;
        }
        v$.value.form.state.$touch();
      }, 150);
    };

    const selectState = (st) => {
      form.state = st.abbreviation;
      stateQuery.value = st.abbreviation;
      showStateSuggestions.value = false;
      stateActiveIndex.value = -1;
      v$.value.form.state.$touch();
    };

    const onStateKeydown = (event) => {
      const list = filteredStateSuggestions.value;
      if (
        !showStateSuggestions.value &&
        (event.key === "ArrowDown" || event.key === "ArrowUp")
      ) {
        showStateSuggestions.value = true;
        return;
      }
      if (event.key === "ArrowDown") {
        event.preventDefault();
        stateActiveIndex.value = Math.min(
          stateActiveIndex.value + 1,
          list.length - 1
        );
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        stateActiveIndex.value = Math.max(stateActiveIndex.value - 1, 0);
      } else if (
        event.key === "Enter" &&
        stateActiveIndex.value >= 0 &&
        list[stateActiveIndex.value]
      ) {
        event.preventDefault();
        selectState(list[stateActiveIndex.value]);
      } else if (event.key === "Escape") {
        showStateSuggestions.value = false;
        stateActiveIndex.value = -1;
      }
    };

    const hydrateFromMember = (member) => {
      if (!member) return;
      form.address1 = member.address1 || member.address || "";
      form.address2 = member.address2 || "";
      form.city = member.city || "";
      form.state = String(member.state || "").toUpperCase();
      stateQuery.value = form.state;
      form.zip = member.zip || "";
      form.county = member.county || "";
      form.address = member.address || "";
      if (form.address) {
        setTimeout(() => {
          const host = document.getElementById("address");
          const el =
            host?.tagName === "INPUT" ? host : host?.querySelector?.("input");
          if (el) el.value = form.address;
        }, 200);
      }
    };

    onMounted(() => {
      // vue-google-autocomplete may render its own internal input; bind it to our label for WAVE/ADA.
      const host = document.getElementById("address");
      const input =
        host?.tagName === "INPUT"
          ? host
          : host?.querySelector?.("input") || document.querySelector('input.pac-target-input');
      if (input) {
        input.id = "address";
        input.setAttribute("aria-labelledby", "address-label");
      }
      hydrateFromMember(props.state.member);
    });

    watch(
      () => props.state.reset_form,
      (reset) => {
        if (reset !== true) return;
        Object.keys(form).forEach((key) => {
          form[key] = "";
        });
        stateQuery.value = "";
        setTimeout(() => {
          const el = document.getElementById("address");
          if (el) el.value = "";
        }, 200);
        v$.value.$reset();
      }
    );

    return {
      form,
      v$,
      isStudentFlow,
      googleAutocompleteFields: GOOGLE_AUTOCOMPLETE_FIELDS,
      stateInput,
      stateQuery,
      showStateSuggestions,
      stateActiveIndex,
      filteredStateSuggestions,
      addressStates,
      onStateInput,
      onStateFocus,
      onStateBlur,
      onStateKeydown,
      selectState,
    };
  },
  components: {
    VueGoogleAutocomplete,
  },
  mounted() {
    this.$nextTick(() => {
      const autocomplete = this.$refs.address?.autocomplete;
      autocomplete?.setFields?.(GOOGLE_AUTOCOMPLETE_FIELDS);
      // Legacy behavior: old :types="['address']" did not apply a strict filter.
      autocomplete?.setTypes?.([]);
    });
  },
  methods: {
    submitForm: async function () {
      this.v$.$touch();
      if (!this.v$.$invalid) {
        if (this.state.opt_type == "SEIU721" && (!this.state.company_number || this.state.company_number === "")) {
          Swal.fire({
            title: "Error!",
            text: "Please select company before proceeding!",
            icon: "error",
            confirmButtonText: "OK",
          });
          return false;
        }
        const address1 = (this.form.address1 || this.form.address || "").trim();
        this.$emit("updateState", {
          key: "member",
          value: {
            ...this.state.member,
            address: this.form.address,
            address1,
            address2: this.form.address2 || "",
            city: this.form.city,
            state: this.form.state,
            zip: this.form.zip || "",
          },
        });
        this.$emit("updateState", { key: "cdl_plan", value: false });
        this.$emit("updateState", { key: "payment_method", value: "" });
        this.$emit("getEffDate");

        const state = filter(this.addressStates, { abbreviation: this.form.state });
        if (state.length == 0) {
          this.$emit("updateState", { key: "disp_enroll_section", value: false });
          Swal.fire({
            title: "Error!",
            text: "State field is invalid.",
            icon: "error",
            confirmButtonText: "OK",
          });
          return false;
        }

        if (this.state.opt_type === "direct_page" || this.state.enroll_option === "affilliated") {
          if (this.state.opt_type === "direct_page") {
            this.$emit("updateState", { key: "company_number", value: this.searchObject.opt });
          }
          if (this.state.enroll_option === "affilliated") {
            this.$emit("updateState", { key: "company_number", value: this.state.company.number });
          }
        }

        const effectiveOptType = getEffectiveOptType(this.state);

        let payload = {
          state: this.form.state,
          opt_type: effectiveOptType,
          referer_url: this.state.referer_url,
          api_name: "getPlanSettings",
          is_list: true,
          city: this.form.city,
          zip: this.form.zip,
        };

        if (checkStudentFlow(this.state)) {
          payload = {
            state: this.form.state,
            opt_type: effectiveOptType,
            referer_url: this.state.referer_url,
            api_name: "getPlanSettings",
            is_list: false,
            city: this.form.city,
            zip: this.form.zip,
            student_school_state: this.form.state,
          };
        } else if (this.state.enroll_option != "individual") {
          payload = {
            company: this.state.company_number,
            opt_type: this.state.opt_type,
            referer_url: this.state.referer_url,
            api_name: "getPlanSettings",
            is_list: false,
            city: this.state.member.city,
            zip: this.state.member.zip,
          };
        }

        if (this.searchObject.opt === "mdsid") {
          this.$emit("updateState", {
            key: "company_number",
            value: this.state.company.company,
          });

          payload = {
            company: this.state.company.company,
            referer_url: this.state.referer_url,
            api_name: "getPlanSettings",
            city: this.state.member.city,
            zip: this.state.member.zip,
          };
        }

        console.log(payload);
        await this.$emit("getPlan", payload);
      }
    },
    resetForm: function () {
        Object.entries(this.form).forEach(([key, val]) => {
          console.log(val)
          this.form[key] = "";
          this.$emit("updateState", { key: "member." + key, value: "" });
        });
     
      setTimeout(() => {
        const el = document.getElementById("address");
        if (el) el.value = "";
        this.$emit("resetForm");
      }, 400);
      this.v$.$reset();
    },
    handlePlaceChange(place, rawPlace) {
      setTimeout(() => {
        let address2;
        const autocompleteText = this.$refs.address?.autocompleteText || "";
        if (autocompleteText) {
          address2 = autocompleteText.split(",");
        }
        if (place.street_number != undefined) {
          this.form.address1 = place.street_number + " " + place.route;
        } else {
          this.form.address1 = place.route;
        }
        if (place.administrative_area_level_2) {
          this.form.county = place.administrative_area_level_2;
        }
        if (address2?.length > 1) {
          if (
            place.locality?.toLowerCase().replace(/\s+/g, "") !=
            address2[1].toLowerCase().replace(/\s+/g, "")
          ) {
            // optional address2 line intentionally left blank
          } else {
            this.form.address2 = "";
          }
        }
        this.form.city = place.locality;
        this.form.state = place.administrative_area_level_1;
        this.stateQuery = place.administrative_area_level_1 || "";
        this.form.zip =
          place.postal_code ||
          getPostalCode(rawPlace, autocompleteText);

        const applyGeocodedZip = () => {
          const location = rawPlace?.geometry?.location;
          if (!location || this.form.zip) return;
          geocodeZipFromLocation(location).then((zip) => {
            if (zip) this.form.zip = zip;
          });
        };

        if (!this.form.zip && rawPlace?.place_id && window.google?.maps?.places) {
          const service = new window.google.maps.places.PlacesService(
            document.createElement("div")
          );
          service.getDetails(
            { placeId: rawPlace.place_id, fields: GOOGLE_AUTOCOMPLETE_FIELDS },
            (details, status) => {
              if (status === window.google.maps.places.PlacesServiceStatus.OK) {
                this.form.zip =
                  getPostalCode(details, autocompleteText) || this.form.zip;
              }
              applyGeocodedZip();
            }
          );
        } else {
          applyGeocodedZip();
        }
      }, 300);
    },
  },
};
</script>


<style scoped>
/* Darken placeholders for WCAG contrast (Address step only). */
.address-info :deep(input::placeholder),
.address-info :deep(textarea::placeholder) {
  color: #6c6c6c !important;
  opacity: 1;
}

/* Darken muted labels for WCAG contrast (Address step only). */
.address-info .address-label {
  color: #595959 !important;
}

/* WCAG: increase contrast for required asterisk only (Address step only). */
.address-info .required-asterisk {
  color: #b00020 !important;
}

/* WCAG: increase contrast for validation error text only (Address step only). */
.address-info :deep(small.text-error) {
  color: #b00020 !important;
}

.student-address-help {
  color: #ba4224;
}

.state-search-wrap {
  position: relative;
}

.state-suggestions {
  position: absolute;
  z-index: 60;
  left: 0;
  right: 0;
  top: calc(100% + 4px);
  max-height: 220px;
  overflow-y: auto;
  margin: 0;
  padding: 0;
  list-style: none;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
}

.state-suggestions li {
  padding: 10px 12px;
  cursor: pointer;
  font-size: 14px;
  color: #1f3341;
}

.state-suggestions li:hover,
.state-suggestions__option--active {
  background: #edf1f8;
}

.state-suggestions__option--active {
  background: #dbe4f2;
}

.address-info-tooltip {
  position: relative;
  display: inline-flex;
  vertical-align: text-top;
  margin-top: 1px;
}

.address-info-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  border: 0;
  border-radius: 9999px;
  background: transparent;
  cursor: help;
  transition: background-color 0.15s ease;
}

.address-info-trigger:hover,
.address-info-trigger:focus-visible {
  background: rgba(186, 66, 36, 0.1);
  outline: none;
}

.address-info-trigger:focus-visible {
  box-shadow: 0 0 0 2px #fff, 0 0 0 3px rgba(186, 66, 36, 0.35);
}

.address-info-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 15px;
  height: 15px;
  border: 1px solid #ba4224;
  border-radius: 50%;
  color: #ba4224;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  font-style: normal;
  font-family: inherit;
  pointer-events: none;
}

.address-info-popover {
  position: absolute;
  top: -12px;
  left: 26px;
  z-index: 50;
  display: block;
  width: min(300px, calc(100vw - 32px));
  overflow: hidden;
  border: 1.5px solid #1a1a1a;
  border-radius: 6px;
  background: #f8fdfd;
  color: #1a1a1a;
  text-align: left;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.12);
  opacity: 0;
  visibility: hidden;
  transform: translateY(-4px);
  transition: opacity 0.15s ease, transform 0.15s ease, visibility 0.15s ease;
  pointer-events: none;
}

.address-info-popover__title {
  display: block;
  padding: 12px;
  background-color: #d9f9f9;
  font-size: 15px;
  line-height: 20px;
  font-weight: 600;
  color: #1a1a1a;
}

.address-info-popover__body {
  display: block;
  padding: 12px;
  background-color: #ffffff;
  font-size: 13px;
  line-height: 18px;
  font-weight: 500;
  color: #1a1a1a;
}

.address-info-tooltip:hover .address-info-popover,
.address-info-tooltip:focus-within .address-info-popover {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

@media (max-width: 991px) {
  .address-info-popover {
    top: calc(100% + 8px);
    left: auto;
    right: 0;
    transform: translateY(-4px);
  }

  .address-info-tooltip:hover .address-info-popover,
  .address-info-tooltip:focus-within .address-info-popover {
    transform: translateY(0);
  }
}
</style>
